'use client';

import { fetchSlugModel } from '@eliadmin/core/src/api/slugs/fetch-slug-model';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { addProductToFavorites } from '@eliadmin/core/src/api/customers/customers-api';

import { Button, type ButtonProps } from '@eliadmin/core/src/components/ui/button';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { checkIfInLocalStorage } from '@eliadmin/core/src/utils/favorite-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { FiHeart } from 'react-icons/fi';

type AddToFavoritesButtonProps = {
  slug: string;
  className?: string;
  isFavorite?: boolean;
  productId?: number;
  showText?: boolean;
  buttonVariant?: ButtonProps['variant'];
  buttonSize?: ButtonProps['size'];
  buttonClassName?: string;
};

// TODO: implement purely with server actions maybe
export function AddToFavoritesButton(props: AddToFavoritesButtonProps) {
  const { me, isInitialized } = useCustomerStore();
  const authToken = me?.authToken;
  const locale = useLocaleStore(s => s.currentLocale);
  const { t } = useTranslations();
  const router = useRouter();
  const queryClient = useQueryClient();
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const productQueryKey = [CacheKey.PRODUCT_DETAIL, locale, props.slug, me?.customer.id ?? 'guest'];

  const { data: product } = useQuery({
    queryKey: productQueryKey,
    queryFn: () =>
      fetchSlugModel({
        model: 'App\\Models\\Product',
        slug: props.slug,
        locale,
        authToken: authToken,
      }),
    enabled: isInitialized && !!authToken && !!props.slug,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  const { mutate, isPending: isMutating } = useMutation({
    mutationFn: addProductToFavorites,
    onSettled: (data, error) => {
      queryClient.invalidateQueries({
        queryKey: [CacheKey.PRODUCT_DETAIL, locale, props.slug],
      });
      queryClient.invalidateQueries({ queryKey: [CacheKey.FAVORITE_PRODUCTS] });
      if (!error) {
        queryClient.setQueryData(productQueryKey, {
          ...product,
          data: {
            ...product?.data,
            is_favorite: !isFavorite,
          },
        });
      }
    },
  });

  const [isFavorite, setIsFavorite] = useState<boolean | undefined>(
    (props.productId &&
      ((!me &&
        templateSettings.product_teaser?.enable_add_to_favorite_when_not_logged_in?.enabled &&
        checkIfInLocalStorage(props.productId)) ||
        product?.data.is_favorite ||
        props.isFavorite)) ??
      false,
  );
  const buttonLabel = props.showText
    ? isFavorite
      ? t('text532')
      : t('text531')
    : isFavorite
      ? `${t('text532')} ${product?.data?.name ?? props.slug}`
      : `${t('text531')} ${product?.data?.name ?? props.slug}`;

  function handleAddToFavorites(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    if (!authToken) {
      if (templateSettings.product_teaser?.enable_add_to_favorite_when_not_logged_in?.enabled) {
        const existingFavorites = JSON.parse(localStorage.getItem('favoriteProducts') || '[]');

        if (existingFavorites.includes(props.productId)) {
          const updatedFavorites = existingFavorites.filter((id: number) => id !== props.productId);

          setIsFavorite(false);

          localStorage.setItem('favoriteProducts', JSON.stringify(updatedFavorites));
          window.dispatchEvent(new Event('storage'));
        } else {
          existingFavorites.push(props.productId);

          setIsFavorite(true);

          localStorage.setItem('favoriteProducts', JSON.stringify(existingFavorites));
          window.dispatchEvent(new Event('storage'));
        }
      } else {
        return router.push(ROUTES.SIGN_IN());
      }
    } else {
      mutate({ productSlug: props.slug, authToken, locale });
      setIsFavorite(!isFavorite);
    }
  }

  return (
    <Button
      variant={props.buttonVariant ?? 'outline'}
      size={props.buttonSize ?? 'icon-sm'}
      onClick={handleAddToFavorites}
      className={cn('px-3', props.buttonClassName)}
      aria-label={buttonLabel}
    >
      {isMutating ? (
        <Spinner className="h-6 w-6" />
      ) : (
        <div className="flex flex-row">
          <FiHeart
            className={cn(
              'h-5 w-5 cursor-pointer transition-colors duration-200 ease-in-out  hover:text-muted-foreground',
              'text-foreground',
              isFavorite
                ? 'fill-destructive-foreground stroke-destructive-foreground'
                : 'fill-none',
              props.className,
            )}
            aria-hidden="true"
            strokeWidth={1.5}
          />
          {props.showText && (
            <span className="link ml-2 underline">{isFavorite ? t('text532') : t('text531')}</span>
          )}
        </div>
      )}
    </Button>
  );
}
